import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNav } from "../../components/NavContext";

const UserLocationsList = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 50, total: 0 });
  const [minUserId, setMinUserId] = useState(500);

  const DOMAIN = process.env.REACT_APP_DOMAIN;
  const API_URL = process.env.REACT_APP_API_URL;

  const { addLink } = useNav();

  useEffect(() => {
    addLink("اطبائنا", `${DOMAIN}control/doctor-list`);
    addLink("User Locations", `${DOMAIN}control/admin/user-locations`);
  }, [addLink, DOMAIN]);

  useEffect(() => {
    document.title = "User Locations";
  }, []);

  useEffect(() => {
    setPage(1);
  }, [perPage, minUserId]);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('per_page', String(perPage));
      params.set('min_user_id', String(minUserId));

      const response = await axios.get(`${API_URL}admin/locations/by-user-id?${params.toString()}`, {
        withCredentials: true,
      });

      setLocations(response.data.data || []);
      setMeta({
        current_page: response.data.current_page || 1,
        last_page: response.data.last_page || 1,
        per_page: response.data.per_page || perPage,
        total: response.data.total || 0,
      });
    } catch (e) {
      console.error("Error fetching locations:", e);
      setError("فشل تحميل المواقع");
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL, page, perPage, minUserId]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  if (loading && locations.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4" dir="rtl">
      <header className="mb-4 text-center">
        <h1 className="display-6 mb-1">مواقع المستخدمين</h1>
        <p className="text-muted mb-0">قائمة المواقع المضافة من قبل المستخدمين (ID &gt; {minUserId})</p>
      </header>

      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <select
            className="form-select"
            value={perPage}
            onChange={(e) => setPerPage(parseInt(e.target.value, 10) || 50)}
          >
            <option value={10}>10 لكل صفحة</option>
            <option value={20}>20 لكل صفحة</option>
            <option value={50}>50 لكل صفحة</option>
            <option value={100}>100 لكل صفحة</option>
          </select>
        </div>
        <div className="col-md-3">
          <input
            type="number"
            className="form-control"
            value={minUserId}
            onChange={(e) => setMinUserId(parseInt(e.target.value, 10) || 500)}
            placeholder="Min User ID"
          />
        </div>
        <div className="col-md-3 text-start">
          <button className="btn btn-primary w-100" onClick={fetchLocations} disabled={loading}>
            {loading ? 'جاري التحميل...' : 'تحديث'}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-striped table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '80px' }}>Location ID</th>
                  <th>اسم الموقع</th>
                  <th style={{ width: '150px' }}>اسم المستخدم</th>
                </tr>
              </thead>
              <tbody>
                {locations && locations.length > 0 ? (
                  locations.map((location) => (
                    <tr key={location.id}>
                      <td className="text-center font-monospace">{location.id}</td>
                      <td>{location.name || 'غير معروف'}</td>
                      <td>{location.user?.name || 'غير معروف'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center py-4">
                      <div className="alert alert-info mb-0">
                        لا توجد مواقع لعرضها
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 gap-2">
        <div className="text-muted">
          الإجمالي: {meta.total ?? 0}
        </div>

        <nav aria-label="Locations pagination">
          <ul className="pagination mb-0">
            <li className={`page-item ${meta.current_page <= 1 ? 'disabled' : ''}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                السابق
              </button>
            </li>

            <li className="page-item disabled">
              <span className="page-link">
                صفحة {meta.current_page} من {meta.last_page}
              </span>
            </li>

            <li className={`page-item ${meta.current_page >= meta.last_page ? 'disabled' : ''}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
              >
                التالي
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default UserLocationsList;
